import { angle, approxZero, EPSILON, type Vec2, vec } from '../math';

import { type Arc, type Circle, type Ellipse } from './types';

/**
 * Operations on circles, arcs and ellipses.
 *
 * The ellipse routines lean on one idea repeatedly: an ellipse is the image of the unit
 * circle under an affine map (scale by `rx, ry`, then rotate). Mapping a problem into
 * that space turns "closest point on ellipse" and "line meets ellipse" into their
 * circular equivalents, which have closed-form answers.
 */

/* ---------------------------------------------------------------------- circle */

/** Point on the circle at `theta` radians from the positive x-axis. */
export function pointOnCircle(c: Circle, theta: number): Vec2 {
  return vec.addScaled(c.center, vec.fromAngle(theta), c.radius);
}

/** Angle of `p` as seen from the circle's centre, in `(-π, π]`. */
export function angleOnCircle(c: Circle, p: Vec2): number {
  return vec.angle(vec.subtract(p, c.center));
}

/**
 * Closest point on the circle's *outline*.
 *
 * A point at the centre is equidistant from every point on the circle; the positive
 * x-direction is returned as an arbitrary but deterministic choice, because returning
 * `NaN` or the centre itself would break callers that expect a point on the curve.
 */
export function closestPointOnCircle(c: Circle, p: Vec2): Vec2 {
  const d = vec.subtract(p, c.center);
  if (vec.isZero(d)) return vec.addScaled(c.center, vec.UNIT_X, c.radius);
  return vec.addScaled(c.center, vec.normalize(d), c.radius);
}

/** Distance to the outline. Zero on the circle, positive both inside and outside. */
export function distanceToCircle(c: Circle, p: Vec2): number {
  return Math.abs(vec.distance(p, c.center) - c.radius);
}

/** Signed distance to the outline: negative inside, positive outside. */
export function signedDistanceToCircle(c: Circle, p: Vec2): number {
  return vec.distance(p, c.center) - c.radius;
}

export function isPointOnCircle(c: Circle, p: Vec2, epsilon: number = EPSILON): boolean {
  return distanceToCircle(c, p) <= epsilon;
}

/** Interior test. The outline counts as inside, within `epsilon`. */
export function circleContains(c: Circle, p: Vec2, epsilon: number = EPSILON): boolean {
  return vec.distance(p, c.center) <= c.radius + epsilon;
}

export function circleCircumference(c: Circle): number {
  return 2 * Math.PI * c.radius;
}

export function circleArea(c: Circle): number {
  return Math.PI * c.radius * c.radius;
}

/**
 * The unique circle through three points, or `null` when they are collinear.
 *
 * Solved as the intersection of two perpendicular bisectors, expanded into a
 * determinant form to avoid constructing intermediate line objects.
 */
export function circleThroughThreePoints(a: Vec2, b: Vec2, c: Vec2): Circle | null {
  const ax = a.x;
  const ay = a.y;
  const bx = b.x;
  const by = b.y;
  const cx = c.x;
  const cy = c.y;

  const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));

  // `d` is twice the signed area of the triangle: zero exactly when collinear. Scale
  // the tolerance with the size of the triangle so that a large but genuinely thin
  // triangle is still rejected, and a small valid one is not.
  const scale = Math.max(
    1,
    vec.distanceSquared(a, b),
    vec.distanceSquared(b, c),
    vec.distanceSquared(a, c)
  );
  if (approxZero(d / scale, EPSILON)) return null;

  const aSq = ax * ax + ay * ay;
  const bSq = bx * bx + by * by;
  const cSq = cx * cx + cy * cy;

  const ux = (aSq * (by - cy) + bSq * (cy - ay) + cSq * (ay - by)) / d;
  const uy = (aSq * (cx - bx) + bSq * (ax - cx) + cSq * (bx - ax)) / d;
  const center = { x: ux, y: uy };
  return { center, radius: vec.distance(center, a) };
}

/* ------------------------------------------------------------------------- arc */

export function arcToCircle(a: Arc): Circle {
  return { center: a.center, radius: a.radius };
}

export function arcStartPoint(a: Arc): Vec2 {
  return vec.addScaled(a.center, vec.fromAngle(a.startAngle), a.radius);
}

export function arcEndPoint(a: Arc): Vec2 {
  return vec.addScaled(a.center, vec.fromAngle(a.endAngle), a.radius);
}

/** Swept angle in `[0, 2π]`, always positive regardless of direction. */
export function arcSweep(a: Arc): number {
  const raw = a.counterclockwise ? a.startAngle - a.endAngle : a.endAngle - a.startAngle;
  if (raw >= angle.TAU - EPSILON) return angle.TAU;
  return angle.normalize(raw);
}

export function arcLength(a: Arc): number {
  return arcSweep(a) * a.radius;
}

/** Point at fraction `t` of the way along the arc, `t` in `[0, 1]`. */
export function pointOnArc(a: Arc, t: number): Vec2 {
  const sweep = arcSweep(a);
  const theta = a.counterclockwise ? a.startAngle - sweep * t : a.startAngle + sweep * t;
  return vec.addScaled(a.center, vec.fromAngle(theta), a.radius);
}

/** Midpoint of the arc — the apex of the bulge, used for arc handles and labels. */
export function arcMidpoint(a: Arc): Vec2 {
  return pointOnArc(a, 0.5);
}

export function isAngleOnArc(a: Arc, theta: number, epsilon: number = EPSILON): boolean {
  return angle.isOnArc(theta, a.startAngle, a.endAngle, a.counterclockwise, epsilon);
}

/**
 * Closest point on the arc.
 *
 * If the radial projection lands within the sweep the answer is that projection;
 * otherwise the closest point is whichever endpoint is nearer. Skipping the endpoint
 * fallback is the usual bug here, and it shows up as an arc that cannot be selected
 * near its ends.
 */
export function closestPointOnArc(a: Arc, p: Vec2): Vec2 {
  const theta = vec.angle(vec.subtract(p, a.center));
  if (isAngleOnArc(a, theta)) {
    return vec.addScaled(a.center, vec.fromAngle(theta), a.radius);
  }
  const start = arcStartPoint(a);
  const end = arcEndPoint(a);
  return vec.distanceSquared(p, start) <= vec.distanceSquared(p, end) ? start : end;
}

export function distanceToArc(a: Arc, p: Vec2): number {
  return vec.distance(p, closestPointOnArc(a, p));
}

export function isPointOnArc(a: Arc, p: Vec2, epsilon: number = EPSILON): boolean {
  return distanceToArc(a, p) <= epsilon;
}

/* --------------------------------------------------------------------- ellipse */

/** Point on the ellipse at parameter `t` (the *eccentric* angle, not the polar angle). */
export function pointOnEllipse(e: Ellipse, t: number): Vec2 {
  const cos = Math.cos(e.rotation);
  const sin = Math.sin(e.rotation);
  const x = e.rx * Math.cos(t);
  const y = e.ry * Math.sin(t);
  return {
    x: e.center.x + x * cos - y * sin,
    y: e.center.y + x * sin + y * cos,
  };
}

/** Maps a world point into the ellipse's unit-circle space. */
export function toUnitCircleSpace(e: Ellipse, p: Vec2): Vec2 {
  const cos = Math.cos(e.rotation);
  const sin = Math.sin(e.rotation);
  const dx = p.x - e.center.x;
  const dy = p.y - e.center.y;
  // Inverse rotation, then inverse scale.
  const rx = dx * cos + dy * sin;
  const ry = -dx * sin + dy * cos;
  return { x: e.rx <= EPSILON ? 0 : rx / e.rx, y: e.ry <= EPSILON ? 0 : ry / e.ry };
}

/** Maps a point from unit-circle space back to world space. */
export function fromUnitCircleSpace(e: Ellipse, p: Vec2): Vec2 {
  const cos = Math.cos(e.rotation);
  const sin = Math.sin(e.rotation);
  const x = p.x * e.rx;
  const y = p.y * e.ry;
  return {
    x: e.center.x + x * cos - y * sin,
    y: e.center.y + x * sin + y * cos,
  };
}

/** Interior test, exact — the unit-circle image of the interior is the unit disc. */
export function ellipseContains(e: Ellipse, p: Vec2, epsilon: number = EPSILON): boolean {
  const u = toUnitCircleSpace(e, p);
  return vec.lengthSquared(u) <= 1 + epsilon;
}

/**
 * Closest point on an origin-centred, axis-aligned ellipse, restricted to the first
 * quadrant. Requires `e0 >= e1 > 0` and `y0, y1 >= 0`.
 *
 * Eberly's formulation. The closest point satisfies
 * `(e0·y0/(t + e0²))² + (e1·y1/(t + e1²))² = 1` for some `t`, and that function is
 * strictly decreasing on the bracketing interval — so bisection converges globally,
 * with no initial guess to get wrong.
 *
 * Newton's method on the squared-distance derivative is the tempting alternative and it
 * is subtly broken: every axis endpoint is a stationary point, so a query on or near an
 * axis converges to the *farthest* point rather than the nearest. That shows up in the
 * editor as a selection highlight snapping to the opposite side of the ellipse.
 */
function closestOnFirstQuadrant(
  e0: number,
  e1: number,
  y0: number,
  y1: number
): readonly [number, number] {
  if (y1 > 0) {
    if (y0 > 0) {
      const esqr0 = e0 * e0;
      const esqr1 = e1 * e1;
      const ey0 = e0 * y0;
      const ey1 = e1 * y1;

      let t0 = -esqr1 + ey1;
      let t1 = -esqr1 + Math.hypot(ey0, ey1);
      let t = t0;

      // 100 halvings exhausts double precision long before the loop ends; the
      // `t === t0 || t === t1` guard exits as soon as the interval stops shrinking.
      for (let i = 0; i < 100; i++) {
        t = 0.5 * (t0 + t1);
        if (t === t0 || t === t1) break;
        const r0 = ey0 / (t + esqr0);
        const r1 = ey1 / (t + esqr1);
        const f = r0 * r0 + r1 * r1 - 1;
        if (f > 0) {
          t0 = t;
        } else if (f < 0) {
          t1 = t;
        } else {
          break;
        }
      }
      return [(esqr0 * y0) / (t + esqr0), (esqr1 * y1) / (t + esqr1)];
    }
    // On the minor axis: the closest point is the minor vertex.
    return [0, e1];
  }

  // On the major axis. Inside the "evolute" cusp the closest point leaves the axis.
  const denominator = e0 * e0 - e1 * e1;
  const numerator = e0 * y0;
  if (numerator < denominator) {
    const ratio = numerator / denominator;
    return [e0 * ratio, e1 * Math.sqrt(Math.max(0, 1 - ratio * ratio))];
  }
  return [e0, 0];
}

/**
 * Closest point on the ellipse outline.
 *
 * Exact to double precision, not an approximation. The query is moved into the
 * ellipse's own frame, folded into the first quadrant by symmetry, solved by
 * `closestOnFirstQuadrant`, then unfolded and rotated back.
 *
 * The naive shortcut — normalise in unit-circle space and map back — is *not* the
 * closest point. For a 10x2 ellipse it can be off by more than a unit, which is plainly
 * visible as a snap indicator that does not sit under the cursor.
 */
export function closestPointOnEllipse(e: Ellipse, p: Vec2): Vec2 {
  if (e.rx <= EPSILON || e.ry <= EPSILON) return e.center;

  const cos = Math.cos(e.rotation);
  const sin = Math.sin(e.rotation);
  const dx = p.x - e.center.x;
  const dy = p.y - e.center.y;

  // Into the ellipse's own axis-aligned frame.
  const localX = dx * cos + dy * sin;
  const localY = -dx * sin + dy * cos;

  const signX = localX < 0 ? -1 : 1;
  const signY = localY < 0 ? -1 : 1;
  const absX = Math.abs(localX);
  const absY = Math.abs(localY);

  // Eberly's solution requires the major radius first; swap the roles when ry is larger.
  let qx: number;
  let qy: number;
  if (e.rx >= e.ry) {
    const [a, b] = closestOnFirstQuadrant(e.rx, e.ry, absX, absY);
    qx = a;
    qy = b;
  } else {
    const [a, b] = closestOnFirstQuadrant(e.ry, e.rx, absY, absX);
    qx = b;
    qy = a;
  }

  const ex = signX * qx;
  const ey = signY * qy;
  return {
    x: e.center.x + ex * cos - ey * sin,
    y: e.center.y + ex * sin + ey * cos,
  };
}

export function distanceToEllipse(e: Ellipse, p: Vec2): number {
  return vec.distance(p, closestPointOnEllipse(e, p));
}

export function isPointOnEllipse(e: Ellipse, p: Vec2, epsilon: number = EPSILON): boolean {
  return distanceToEllipse(e, p) <= epsilon;
}

/**
 * Perimeter, via Ramanujan's second approximation.
 *
 * The exact value is a complete elliptic integral of the second kind with no elementary
 * closed form. Ramanujan's formula has relative error below 1e-10 for eccentricities up
 * to about 0.99 — far finer than any measurement a diagram will display.
 */
export function ellipsePerimeter(e: Ellipse): number {
  const a = e.rx;
  const b = e.ry;
  if (a <= EPSILON && b <= EPSILON) return 0;
  const h = ((a - b) * (a - b)) / ((a + b) * (a + b));
  return Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
}

export function ellipseArea(e: Ellipse): number {
  return Math.PI * e.rx * e.ry;
}

/** The two focal points. Coincident with the centre for a circle. */
export function ellipseFoci(e: Ellipse): readonly [Vec2, Vec2] {
  const major = Math.max(e.rx, e.ry);
  const minor = Math.min(e.rx, e.ry);
  const c = Math.sqrt(Math.max(0, major * major - minor * minor));
  // The focal axis runs along whichever radius is larger.
  const axisAngle = e.rx >= e.ry ? e.rotation : e.rotation + Math.PI / 2;
  const offset = vec.fromAngle(axisAngle, c);
  return [vec.subtract(e.center, offset), vec.add(e.center, offset)];
}
